import CurrencyBadgeUi from 'common/components/entities/OfferPriceNew/ui/CurrencyBadgeUi'
import { CouponTypeEnum } from 'common/enums/CouponTypeEnum'
import { Coupon } from 'common/types/CouponType'
import { ProductInterface } from 'common/types/entities/ProductInterface'
import React from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { CalculatePriceResponse } from 'src/shared/types/pricing-types'
import {
  centsToEuro,
  checkIsTaxInclusive,
  getReadablePrice,
} from 'src/shared/utils/price-calculator'
import PricePlanAmountUi from '../ui/PricePlanAmountUi'
import PricePlanItemUi from '../ui/PricePlanItemUi'
import PricePlanNameUi from '../ui/PricePlanNameUi'
import PricePlanUi from '../ui/PricePlanUi'

export interface ProductProps {
  product?: ProductInterface | null
  mainOfferPricing?: CalculatePriceResponse
  coupon?: Coupon
  isFinalSummary?: boolean
  nameFontFamily?: string
  mobileNameFontFamily?: string
  nameFontSize: number
  mobileNameFontSize?: number
  nameFontStyle?: string
  mobileNameFontStyle?: string
  nameFontWeight?: string
  mobileNameFontWeight?: string
  nameLineHeight: number
  mobileNameLineHeight?: number
  nameColor: string
  mobileNameColor?: string
  amountFontFamily?: string
  mobileAmountFontFamily?: string
  amountFontSize: number
  mobileAmountFontSize?: number
  amountFontStyle?: string
  mobileAmountFontStyle?: string
  amountFontWeight?: string
  mobileAmountFontWeight?: string
  amountLineHeight: number
  mobileAmountLineHeight?: number
  amountColor: string
  mobileAmountColor?: string
  locale: string
}

const Product = ({
  mainOfferPricing,
  isFinalSummary = true,
  product,
  coupon,
  nameFontSize,
  mobileNameFontSize,
  nameFontStyle,
  mobileNameFontStyle,
  nameFontWeight,
  mobileNameFontWeight,
  nameFontFamily,
  mobileNameFontFamily,
  nameLineHeight,
  mobileNameLineHeight,
  nameColor,
  mobileNameColor,
  amountFontSize,
  mobileAmountFontSize,
  amountFontStyle,
  mobileAmountFontStyle,
  amountFontWeight,
  mobileAmountFontWeight,
  amountFontFamily,
  mobileAmountFontFamily,
  amountLineHeight,
  mobileAmountLineHeight,
  amountColor,
  mobileAmountColor,
  locale,
}: ProductProps) => {
  const { t } = useTypedTranslation(['common', 'publisher'])
  const needTotal = product?.name && (mainOfferPricing?.tax.amount || coupon)
  // temporary hack till next week, gross contains tax and net contains discount in addition to inclusive tax issue
  const quantifiedOriginalPrice =
    (mainOfferPricing?.originalAmount ?? 0) * (mainOfferPricing?.quantity ?? 1)

  return (
    <PricePlanUi>
      {product?.name && (
        <>
          <PricePlanItemUi skipLeftOffset>
            <PricePlanNameUi
              fontSize={nameFontSize}
              mobileFontSize={mobileNameFontSize}
              fontStyle={nameFontStyle}
              mobileFontStyle={mobileNameFontStyle}
              fontWeight={nameFontWeight}
              mobileFontWeight={mobileNameFontWeight}
              fontFamily={nameFontFamily}
              mobileFontFamily={mobileNameFontFamily}
              lineHeight={nameLineHeight}
              mobileLineHeight={mobileNameLineHeight}
              color={nameColor}
              mobileColor={mobileNameColor}
            >
              {product.name}
            </PricePlanNameUi>
            <PricePlanAmountUi
              fontSize={amountFontSize}
              mobileFontSize={mobileAmountFontSize}
              fontStyle={amountFontStyle}
              mobileFontStyle={mobileAmountFontStyle}
              fontWeight={amountFontWeight}
              mobileFontWeight={mobileAmountFontWeight}
              fontFamily={amountFontFamily}
              mobileFontFamily={mobileAmountFontFamily}
              lineHeight={amountLineHeight}
              mobileLineHeight={mobileAmountLineHeight}
              color={amountColor}
              mobileColor={mobileAmountColor}
            >
              <CurrencyBadgeUi
                color={amountColor}
                mobileColor={mobileAmountColor}
              >
                {product.currency}
              </CurrencyBadgeUi>
              {getReadablePrice(
                centsToEuro(quantifiedOriginalPrice),
                product.currency,
                locale,
              )}
            </PricePlanAmountUi>
          </PricePlanItemUi>
          {coupon && (
            <PricePlanItemUi skipLeftOffset>
              <PricePlanNameUi
                fontSize={nameFontSize}
                mobileFontSize={mobileNameFontSize}
                fontStyle={nameFontStyle}
                mobileFontStyle={mobileNameFontStyle}
                fontWeight={nameFontWeight}
                mobileFontWeight={mobileNameFontWeight}
                fontFamily={nameFontFamily}
                mobileFontFamily={mobileNameFontFamily}
                lineHeight={nameLineHeight}
                mobileLineHeight={mobileNameLineHeight}
                color={nameColor}
                mobileColor={mobileNameColor}
                data-test-id={`offer-price-product-${product.name}`}
              >
                {t('components.offer_price.one_shots_summary.discount')}
                {coupon.discountType === CouponTypeEnum.Percent &&
                  ` (-${mainOfferPricing?.discount.percent}%)`}
              </PricePlanNameUi>
              <PricePlanAmountUi
                fontSize={amountFontSize}
                mobileFontSize={mobileAmountFontSize}
                fontStyle={amountFontStyle}
                mobileFontStyle={mobileAmountFontStyle}
                fontWeight={amountFontWeight}
                mobileFontWeight={mobileAmountFontWeight}
                fontFamily={amountFontFamily}
                mobileFontFamily={mobileAmountFontFamily}
                lineHeight={amountLineHeight}
                mobileLineHeight={mobileAmountLineHeight}
                color={amountColor}
                mobileColor={mobileAmountColor}
              >
                <CurrencyBadgeUi
                  color={amountColor}
                  mobileColor={mobileAmountColor}
                >
                  {coupon.currency}
                </CurrencyBadgeUi>
                {` -${getReadablePrice(
                  centsToEuro(mainOfferPricing?.discount.amount ?? 0),
                  coupon.currency,
                  locale,
                )}`}
              </PricePlanAmountUi>
            </PricePlanItemUi>
          )}
          {!!mainOfferPricing?.tax.amount && (
            <PricePlanItemUi skipLeftOffset>
              <PricePlanNameUi
                fontSize={nameFontSize}
                mobileFontSize={mobileNameFontSize}
                fontStyle={nameFontStyle}
                mobileFontStyle={mobileNameFontStyle}
                fontWeight={nameFontWeight}
                mobileFontWeight={mobileNameFontWeight}
                fontFamily={nameFontFamily}
                mobileFontFamily={mobileNameFontFamily}
                lineHeight={nameLineHeight}
                mobileLineHeight={mobileNameLineHeight}
                color={nameColor}
                mobileColor={mobileNameColor}
              >
                {`${t('components.offer_price.vat')} (${
                  mainOfferPricing.tax.rate
                }%)`}
                {checkIsTaxInclusive(product.pricePlan) &&
                  ` (${t('components.offer_price.tax_included')})`}
              </PricePlanNameUi>
              <PricePlanAmountUi
                fontSize={amountFontSize}
                mobileFontSize={mobileAmountFontSize}
                fontStyle={amountFontStyle}
                mobileFontStyle={mobileAmountFontStyle}
                fontWeight={amountFontWeight}
                mobileFontWeight={mobileAmountFontWeight}
                fontFamily={amountFontFamily}
                mobileFontFamily={mobileAmountFontFamily}
                lineHeight={amountLineHeight}
                mobileLineHeight={mobileAmountLineHeight}
                color={amountColor}
                mobileColor={mobileAmountColor}
              >
                <CurrencyBadgeUi
                  color={amountColor}
                  mobileColor={mobileAmountColor}
                >
                  {product.currency}
                </CurrencyBadgeUi>
                {getReadablePrice(
                  centsToEuro(mainOfferPricing.tax.amount),
                  product.currency,
                  locale,
                )}
              </PricePlanAmountUi>
            </PricePlanItemUi>
          )}
        </>
      )}
      {!!needTotal && (
        <PricePlanItemUi skipLeftOffset>
          <PricePlanNameUi
            fontSize={nameFontSize}
            mobileFontSize={mobileNameFontSize}
            fontStyle={nameFontStyle}
            mobileFontStyle={mobileNameFontStyle}
            fontWeight={nameFontWeight}
            mobileFontWeight={mobileNameFontWeight}
            fontFamily={nameFontFamily}
            mobileFontFamily={mobileNameFontFamily}
            lineHeight={nameLineHeight}
            mobileLineHeight={mobileNameLineHeight}
            color={nameColor}
            mobileColor={mobileNameColor}
          >
            {isFinalSummary
              ? t('components.offer_price.total')
              : t('components.offer_price.subtotal')}
          </PricePlanNameUi>
          <PricePlanAmountUi
            fontSize={amountFontSize}
            mobileFontSize={mobileAmountFontSize}
            fontStyle={amountFontStyle}
            mobileFontStyle={mobileAmountFontStyle}
            fontWeight={amountFontWeight}
            mobileFontWeight={mobileAmountFontWeight}
            fontFamily={amountFontFamily}
            mobileFontFamily={mobileAmountFontFamily}
            lineHeight={amountLineHeight}
            mobileLineHeight={mobileAmountLineHeight}
            color={amountColor}
            mobileColor={mobileAmountColor}
          >
            <CurrencyBadgeUi
              color={amountColor}
              mobileColor={mobileAmountColor}
            >
              {product.currency}
            </CurrencyBadgeUi>
            {getReadablePrice(
              centsToEuro(mainOfferPricing?.grossAmount ?? 0),
              product.currency,
              locale,
            )}
          </PricePlanAmountUi>
        </PricePlanItemUi>
      )}
    </PricePlanUi>
  )
}
export default Product
